import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";

const ALL = "__all__";

export interface InboxAccountFilterProps {
  /** The accounts that have conversations: id → label (see useInboxList). */
  accounts: ReadonlyMap<string, string | null>;
  /** Selected account id, or null for "all accounts". */
  value: string | null;
  onChange: (accountId: string | null) => void;
  /** Classes for the select itself — the parent lays the filters out. */
  className?: string;
}

/**
 * Account switcher for the inbox. The menu lists the accounts that have
 * conversations; the parent does the filtering client-side.
 *
 * Hidden when there's nothing to switch between (0–1 accounts) — a one-account
 * inbox is already "just that account". A chosen account always shows: hidden,
 * it could not be un-chosen.
 */
export function InboxAccountFilter({
  accounts,
  value,
  onChange,
  className,
}: InboxAccountFilterProps) {
  const { t } = useTranslation();

  const sorted = useMemo(
    () =>
      Array.from(accounts, ([id, label]) => ({
        id,
        label: label ?? t("inbox.account_unnamed"),
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [accounts, t],
  );

  if (sorted.length <= 1 && value === null) return null;

  const options: SimpleSelectOption[] = [
    { value: ALL, label: t("inbox.account_all") },
    ...sorted.map((a) => ({ value: a.id, label: a.label })),
  ];

  return (
    <SimpleSelect
      className={className}
      options={options}
      value={value ?? ALL}
      onValueChange={(v) => onChange(v === ALL ? null : v)}
    />
  );
}
InboxAccountFilter.displayName = "InboxAccountFilter";
